import React, { useEffect, useMemo, useState } from "react";
import { View, Text, Modal, TouchableOpacity, Image } from "react-native";
import { AppImages } from "../utility/AppImages";
import { AppColors } from "../utility/AppColors";
import { AppFontStyle } from "../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { CropImage } from "./choose-avatar-components/CropImage";
import ImageComponent from "./ImageComponent";
import { logPublicVoteForEvent } from "../services/eventsAPI";
import { useLiveStream } from "../context/LiveStreamContext";
import { toast } from "react-toastify";
import Avatar from "./choose-avatar-components/Avatar";
import App from "../../App";

interface VotePopupProps {
  visible: boolean;
  onClose: () => void;
  pollEndTime: string;
  eventDetails: any;
  setIsVoted: (value: boolean) => void;
}

const VotePopup: React.FC<VotePopupProps> = ({
  visible,
  onClose,
  pollEndTime,
  eventDetails,
  setIsVoted,
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState<
    "player1" | "player2" | null
  >(null);
  const [countdown, setCountdown] = useState("");
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const { loginData } = useLiveStream();
  const [showLoader, setShowLoader] = useState(false);
  const onVote = async () => {
    if (!selectedPlayer) return;
    const voteValue =
      selectedPlayer === "player1"
        ? eventDetails.organiserId
        : eventDetails.challengerId;
    try {
      setShowLoader(true);
      const response = await logPublicVoteForEvent(
        loginData?.accessToken,
        eventDetails.eventID,
        voteValue
      );
      if (
        response.statusCode === 201 &&
        response.body?.message === "Vote submitted successfully."
      ) {
        toast.success("Vote submitted successfully.");
        setIsVoted(true);
        setShowLoader(false);
        onClose();
      } else {
        toast.error("Failed to submit vote. Please try again.");
        setShowLoader(false);
      }
    } catch (error) {
      console.error("Error logging public vote:", error);
      toast.error("Vote submission failed.");
      setShowLoader(false);
    }
  };

  useEffect(() => {
    if (!pollEndTime) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const endTime = new Date(pollEndTime).getTime() + 5 * 60 * 1000; // Add 5 minutes to pollEndTime
      const timeLeft = endTime - now;
      if (timeLeft <= 0) {
        clearInterval(interval);
        setCountdown("Poll closed");
        onClose();
      } else {
        const minutes = Math.floor((timeLeft / (1000 * 60)) % 60);
        const seconds = Math.floor((timeLeft / 1000) % 60);
        setCountdown(`${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [pollEndTime]);
  const { audienceData } = useLiveStream();

  const participantDetails = audienceData?.participantDetails || [];

  const organizer = useMemo(() => {
    return (
      participantDetails.find(
        (participant: any) => participant?.userID === eventDetails?.organiserId
      ) || {}
    );
  }, [participantDetails, eventDetails?.organiserId]);

  const challenger = useMemo(() => {
    return (
      participantDetails.find(
        (participant: any) => participant?.userID !== eventDetails?.organiserId
      ) || {}
    );
  }, [participantDetails, eventDetails?.organiserId]);
  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.headerText}>Help Decide the Winner!</Text>
          <View style={styles.seprator} />
          <View style={styles.innerConatiner}>
            <View style={styles.descriptionBox}>
              <Text style={styles.subText}>
                Neither participant has submitted a result. Your vote will help
                us determine the winner of{" "}
                <Text style={styles.gameTitle}>Game Show</Text>.
              </Text>
            </View>
            <Text style={styles.selectionText}>Select who wins the game?</Text>

            <View style={styles.playersContainer}>
              {/* Kaller Section */}
              <View style={styles.playerSection}>
                <Text style={styles.roleLabel}>Kaller</Text>
                <TouchableOpacity
                  style={[
                    styles.playerOuterBox,
                    selectedPlayer === "player1" && styles.selectedPlayer,
                  ]}
                  onPress={() => setSelectedPlayer("player1")}
                >
                  <View style={styles.playerInnerBox}>
                    <View style={styles.avatarCircle}>
                      <Avatar
                        variant="profileLarge"
                        src={
                          organizer.profilePicture === ""
                            ? "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                            : organizer.profilePicture
                        }
                      />
                    </View>
                    <Text style={styles.playerName}>
                      {eventDetails.organiserId}
                    </Text>
                  </View>
                  {selectedPlayer === "player1" && (
                    <View style={styles.radioButton} />
                  )}
                </TouchableOpacity>
              </View>

              <ImageComponent url={AppImages.icVsLogo} style={styles.vsImage} />

              {/* Being Kalled out Section */}
              <View style={styles.playerSection}>
                <Text style={styles.roleLabel}>Opponent</Text>
                <TouchableOpacity
                  style={[
                    styles.playerOuterBox,
                    selectedPlayer === "player2" && styles.selectedPlayer,
                  ]}
                  onPress={() => setSelectedPlayer("player2")}
                >
                  <View style={styles.playerInnerBox}>
                    <View style={styles.avatarCircle}>
                      <Avatar
                        variant="profileLarge"
                        src={
                          challenger.profilePicture === ""
                            ? "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                            : challenger.profilePicture
                        }
                      />
                    </View>
                    <Text style={styles.playerName}>
                      {eventDetails.challengerId}
                    </Text>
                  </View>
                  {selectedPlayer === "player2" && (
                    <View style={styles.radioButton} />
                  )}
                </TouchableOpacity>
              </View>
            </View>

            <Text style={styles.timerText}>Poll closes in {countdown}</Text>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.abstainButton]}
                onPress={() => {
                  // Abstain does nothing – popup remains open
                }}
              >
                <Text style={styles.abstainText}>Abstain</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.button,
                  {
                    backgroundColor:
                      !selectedPlayer || showLoader
                        ? AppColors.grayColor_505050
                        : AppColors.appViolentColor_6D08C3,
                  },
                ]}
                onPress={onVote}
                disabled={!selectedPlayer || showLoader}
              >
                <Text style={styles.voteText}>Vote Now</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* Styles */
const useStyles = CreateResponsiveStyle(
  {
    overlay: {
      flex: 1,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      justifyContent: "center",
      alignItems: "center",
    },
    container: {
      width: "50%",
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      background: "linear-gradient(135deg, #111827 0%, #000 100%)",
      backgroundColor: "#111827",
      padding: 20,
    },
    innerConatiner: {
      backgroundColor: AppColors.purple_453B50,
      alignItems: "center",
      borderRadius: 10,
      paddingHorizontal: 35,
      paddingVertical: 35,
    },
    seprator: {
      height: 2,
      backgroundColor: AppColors.gray_1E1E1E,
      marginBottom: 15,
    },
    playerInnerBox: {
      borderRadius: 11.668,
      borderWidth: 1.167,
      borderColor: "#374151",
      background: "linear-gradient(135deg, rgba(31, 41, 55, 0.50) 0%, rgba(17, 24, 39, 0.50) 100%)",
      backgroundColor: "rgba(31, 41, 55, 0.50)",
      alignItems: "center",
      padding: 12,
    },
    headerText: {
      fontSize: 22,
      fontWeight: "bold",
      color: AppColors.white,
      marginBottom: 10,
    },
    descriptionBox: {
      borderRadius: 8,
      borderWidth: 0.8,
      borderColor: "rgba(239, 68, 68, 0.30)",
      backgroundColor: "rgba(127, 29, 29, 0.20)",
      minHeight: 73,
      padding: 16,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 20,
    },
    subText: {
      color: AppColors.buttonRed_DC2626,
      fontFamily: "Anybody_500Medium",
      fontSize: 18,
      textAlign: "center",
    },
    gameTitle: {
      fontWeight: "bold",
    },
    selectionText: {
      color: AppColors.white,
      ...AppFontStyle.BOLD_ANYBODY_26,
      marginVertical: 20,
    },
    playersContainer: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      width: "75%",
      marginBottom: 15,
      marginTop: 10,
    },
    playerSection: {
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
    },
    roleLabel: {
      ...AppFontStyle.BOLD_ANYBODY_18,
      color: "#DC2626",
      marginBottom: 8,
    },
    playerOuterBox: {
      minHeight: 168,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 17,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "transparent",
      backgroundColor: "transparent",
    },
    selectedPlayer: {
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "#DC2626",
      backgroundColor: "rgba(220, 38, 38, 0.20)",
      shadowColor: "rgba(10, 13, 18, 0.05)",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 1,
      shadowRadius: 2,
      elevation: 1,
    },
    avatarCircle: {
      width: 106,
      height: 106,
      borderRadius: 71.5,
      borderWidth: 0.822,
      borderColor: "#D2D2D2",
      overflow: "hidden",
      justifyContent: "center",
      alignItems: "center",
    },
    playerLabel: {
      ...AppFontStyle.BOLD_ANYBODY_22,
      color: AppColors.white,
      marginBottom: 10,
    },
    playerImage: {
      width: 80,
      height: 80,
      borderRadius: 8,
    },
    playerName: {
      ...AppFontStyle.BOLD_ANYBODY_14,
      color: AppColors.white,
      marginVertical: 10,
      textAlign: "center",
    },
    radioButton: {
      width: 15,
      height: 15,
      borderRadius: 10,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      position: "absolute",
      top: 10,
      right: 10,
    },
    vsImage: {
      width: 131,
      height: 127,
    },
    timerText: {
      fontSize: 14,
      color: AppColors.white,
      marginBottom: 15,
    },
    buttonContainer: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "40%",
    },
    button: {
      flex: 1,
      paddingVertical: 10,
      borderRadius: 10,
      alignItems: "center",
      marginHorizontal: 5,
    },
    abstainButton: {
      backgroundColor: "transparent",
      borderWidth: 2,
      borderColor: AppColors.appViolentColor_6D08C3,
    },
    abstainText: {
      color: AppColors.white,
      fontSize: 14,
      fontWeight: "bold",
    },
    voteButton: {
      backgroundColor: AppColors.appViolentColor_6D08C3,
    },
    voteText: {
      color: AppColors.white,
      fontSize: 14,
      fontWeight: "bold",
    },
  },
  {
    [DEVICE_SIZES.XS]: {
      overlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
      },
      container: {
        width: "90%",
        backgroundColor: AppColors.black_121212,
        borderRadius: 10,
        padding: 20,
      },
      innerConatiner: {
        backgroundColor: AppColors.purple_453B50,
        alignItems: "center",
        borderRadius: 10,
        paddingHorizontal: 5,
        paddingVertical: 10,
      },
      seprator: {
        height: 2,
        backgroundColor: AppColors.gray_1E1E1E,
        marginBottom: 10,
      },
      playerInnerBox: {
        borderRadius: 11.668,
        borderWidth: 1.167,
        borderColor: "#374151",
        backgroundColor: "rgba(31, 41, 55, 0.50)",
        alignItems: "center",
        padding: 10,
      },
      headerText: {
        fontSize: 22,
        fontWeight: "bold",
        color: AppColors.white,
        marginBottom: 10,
      },
      descriptionBox: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "rgba(239, 68, 68, 0.30)",
        backgroundColor: "rgba(127, 29, 29, 0.20)",
        minHeight: 60,
        padding: 12,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 15,
      },
      subText: {
        color: AppColors.buttonRed_DC2626,
        fontFamily: "Anybody_500Medium",
        fontSize: 14,
        textAlign: "center",
      },
      gameTitle: {
        fontWeight: "bold",
      },
      selectionText: {
        color: AppColors.white,
        ...AppFontStyle.BOLD_ANYBODY_18,
        marginVertical: 20,
      },
      playersContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        // marginBottom: 15,
        marginTop: 10,
      },
      vsImage: {
        width: 80,
        height: 80,
      },
      playerOuterBox: {
        minHeight: 150,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "transparent",
        backgroundColor: "transparent",
      },
      selectedPlayer: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#DC2626",
        backgroundColor: "rgba(220, 38, 38, 0.20)",
        shadowColor: "rgba(10, 13, 18, 0.05)",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 1,
        shadowRadius: 2,
        elevation: 1,
      },
      avatarCircle: {
        width: 90,
        height: 90,
        borderRadius: 60,
        borderWidth: 0.822,
        borderColor: "#D2D2D2",
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
      },
      playerLabel: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginBottom: 5,
      },
      playerImage: {
        width: 80,
        height: 80,
        borderRadius: 8,
      },
      playerName: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginVertical: 10,
        textAlign: "center",
      },
      radioButton: {
        width: 15,
        height: 15,
        borderRadius: 10,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        position: "absolute",
        top: 10,
        right: 10,
      },

      timerText: {
        fontSize: 14,
        color: AppColors.white,
        marginBottom: 15,
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "80%",
      },
      button: {
        flex: 1,
        paddingVertical: 5,
        borderRadius: 10,
        alignItems: "center",
        marginHorizontal: 5,
      },
      abstainButton: {
        backgroundColor: "transparent",
        borderWidth: 2,
        borderColor: AppColors.appViolentColor_6D08C3,
      },
      abstainText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
      voteButton: {
        backgroundColor: AppColors.appViolentColor_6D08C3,
      },
      voteText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    [DEVICE_SIZES.SM]: {
      overlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
      },
      container: {
        width: "70%",
        backgroundColor: AppColors.black_121212,
        borderRadius: 10,
        padding: 20,
      },
      innerConatiner: {
        backgroundColor: AppColors.purple_453B50,
        alignItems: "center",
        borderRadius: 10,
        paddingHorizontal: 5,
        paddingVertical: 10,
      },
      seprator: {
        height: 2,
        backgroundColor: AppColors.gray_1E1E1E,
        marginBottom: 10,
      },
      playerInnerBox: {
        borderRadius: 11.668,
        borderWidth: 1.167,
        borderColor: "#374151",
        backgroundColor: "rgba(31, 41, 55, 0.50)",
        alignItems: "center",
        padding: 10,
      },
      headerText: {
        fontSize: 22,
        fontWeight: "bold",
        color: AppColors.white,
        marginBottom: 10,
      },
      descriptionBox: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "rgba(239, 68, 68, 0.30)",
        backgroundColor: "rgba(127, 29, 29, 0.20)",
        minHeight: 65,
        padding: 14,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18,
      },
      subText: {
        color: AppColors.buttonRed_DC2626,
        fontFamily: "Anybody_500Medium",
        fontSize: 16,
        textAlign: "center",
      },
      gameTitle: {
        fontWeight: "bold",
      },
      selectionText: {
        color: AppColors.white,
        ...AppFontStyle.BOLD_ANYBODY_18,
        marginVertical: 20,
      },
      playersContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        // marginBottom: 15,
        marginTop: 10,
      },
      vsImage: {
        width: 80,
        height: 80,
      },
      playerOuterBox: {
        minHeight: 150,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "transparent",
        backgroundColor: "transparent",
      },
      selectedPlayer: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#DC2626",
        backgroundColor: "rgba(220, 38, 38, 0.20)",
        shadowColor: "rgba(10, 13, 18, 0.05)",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 1,
        shadowRadius: 2,
        elevation: 1,
      },
      avatarCircle: {
        width: 90,
        height: 90,
        borderRadius: 60,
        borderWidth: 0.822,
        borderColor: "#D2D2D2",
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
      },
      playerLabel: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginBottom: 5,
      },
      playerImage: {
        width: 80,
        height: 80,
        borderRadius: 8,
      },
      playerName: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginVertical: 10,
        textAlign: "center",
      },
      radioButton: {
        width: 15,
        height: 15,
        borderRadius: 10,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        position: "absolute",
        top: 10,
        right: 10,
      },

      timerText: {
        fontSize: 14,
        color: AppColors.white,
        marginBottom: 15,
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "80%",
      },
      button: {
        flex: 1,
        paddingVertical: 5,
        borderRadius: 10,
        alignItems: "center",
        marginHorizontal: 5,
      },
      abstainButton: {
        backgroundColor: "transparent",
        borderWidth: 2,
        borderColor: AppColors.appViolentColor_6D08C3,
      },
      abstainText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
      voteButton: {
        backgroundColor: AppColors.appViolentColor_6D08C3,
      },
      voteText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    [DEVICE_SIZES.MD]: {
      overlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
      },
      container: {
        width: "80%",
        backgroundColor: AppColors.black_121212,
        borderRadius: 10,
        padding: 20,
      },
      innerConatiner: {
        backgroundColor: AppColors.purple_453B50,
        alignItems: "center",
        borderRadius: 10,

        paddingVertical: 35,
      },
      seprator: {
        height: 2,
        backgroundColor: AppColors.gray_1E1E1E,
        marginBottom: 15,
      },
      playerInnerBox: {
        borderRadius: 11.668,
        borderWidth: 1.167,
        borderColor: "#374151",
        backgroundColor: "rgba(31, 41, 55, 0.50)",
        alignItems: "center",
        padding: 10,
      },
      headerText: {
        fontSize: 22,
        fontWeight: "bold",
        color: AppColors.white,
        marginBottom: 10,
      },
      descriptionBox: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "rgba(239, 68, 68, 0.30)",
        backgroundColor: "rgba(127, 29, 29, 0.20)",
        minHeight: 73,
        padding: 16,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20,
      },
      subText: {
        color: AppColors.buttonRed_DC2626,
        fontFamily: "Anybody_500Medium",
        fontSize: 18,
        textAlign: "center",
      },
      gameTitle: {
        fontWeight: "bold",
      },
      selectionText: {
        color: AppColors.white,
        ...AppFontStyle.BOLD_ANYBODY_26,
        marginVertical: 20,
      },
      playersContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "90%",
        marginBottom: 15,
        marginTop: 10,
      },
      playerOuterBox: {
        minHeight: 168,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 17,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "transparent",
        backgroundColor: "transparent",
      },
      selectedPlayer: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#DC2626",
        backgroundColor: "rgba(220, 38, 38, 0.20)",
        shadowColor: "rgba(10, 13, 18, 0.05)",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 1,
        shadowRadius: 2,
        elevation: 1,
      },
      avatarCircle: {
        width: 106,
        height: 106,
        borderRadius: 71.5,
        borderWidth: 0.822,
        borderColor: "#D2D2D2",
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
      },
      playerLabel: {
        ...AppFontStyle.BOLD_ANYBODY_22,
        color: AppColors.white,
        marginBottom: 10,
      },
      playerImage: {
        width: 80,
        height: 80,
        borderRadius: 8,
      },
      playerName: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginVertical: 10,
        textAlign: "center",
      },
      radioButton: {
        width: 15,
        height: 15,
        borderRadius: 10,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        position: "absolute",
        top: 10,
        right: 10,
      },
      // vsText: {
      //   fontSize: 20,
      //   fontWeight: "bold",
      //   color: AppColors.white,
      //   marginHorizontal: 10,
      // },
      timerText: {
        fontSize: 14,
        color: AppColors.white,
        marginBottom: 15,
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "40%",
      },
      button: {
        flex: 1,
        paddingVertical: 10,
        borderRadius: 10,
        alignItems: "center",
        marginHorizontal: 5,
      },
      abstainButton: {
        backgroundColor: "transparent",
        borderWidth: 2,
        borderColor: AppColors.appViolentColor_6D08C3,
      },
      abstainText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
      voteButton: {
        backgroundColor: AppColors.appViolentColor_6D08C3,
      },
      voteText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
    },

    [DEVICE_SIZES.LG]: {
      overlay: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
      },
      container: {
        width: "80%",
        backgroundColor: AppColors.black_121212,
        borderRadius: 10,
        padding: 20,
      },
      innerConatiner: {
        backgroundColor: AppColors.purple_453B50,
        alignItems: "center",
        borderRadius: 10,

        paddingVertical: 35,
      },
      seprator: {
        height: 2,
        backgroundColor: AppColors.gray_1E1E1E,
        marginBottom: 15,
      },
      playerInnerBox: {
        borderRadius: 11.668,
        borderWidth: 1.167,
        borderColor: "#374151",
        backgroundColor: "rgba(31, 41, 55, 0.50)",
        alignItems: "center",
        padding: 10,
      },
      headerText: {
        fontSize: 22,
        fontWeight: "bold",
        color: AppColors.white,
        marginBottom: 10,
      },
      descriptionBox: {
        borderRadius: 8,
        borderWidth: 0.8,
        borderColor: "rgba(239, 68, 68, 0.30)",
        backgroundColor: "rgba(127, 29, 29, 0.20)",
        minHeight: 73,
        padding: 16,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 20,
      },
      subText: {
        color: AppColors.buttonRed_DC2626,
        fontFamily: "Anybody_500Medium",
        fontSize: 18,
        textAlign: "center",
      },
      gameTitle: {
        fontWeight: "bold",
      },
      selectionText: {
        color: AppColors.white,
        ...AppFontStyle.BOLD_ANYBODY_26,
        marginVertical: 20,
      },
      playersContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "70%",
        marginBottom: 15,
        marginTop: 10,
      },
      playerOuterBox: {
        minHeight: 168,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 17,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "transparent",
        backgroundColor: "transparent",
      },
      selectedPlayer: {
        borderRadius: 8,
        borderWidth: 1,
        borderColor: "#DC2626",
        backgroundColor: "rgba(220, 38, 38, 0.20)",
        shadowColor: "rgba(10, 13, 18, 0.05)",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 1,
        shadowRadius: 2,
        elevation: 1,
      },
      avatarCircle: {
        width: 106,
        height: 106,
        borderRadius: 71.5,
        borderWidth: 0.822,
        borderColor: "#D2D2D2",
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
      },
      playerLabel: {
        ...AppFontStyle.BOLD_ANYBODY_22,
        color: AppColors.white,
        marginBottom: 10,
      },
      playerImage: {
        width: 80,
        height: 80,
        borderRadius: 8,
      },
      playerName: {
        ...AppFontStyle.BOLD_ANYBODY_14,
        color: AppColors.white,
        marginVertical: 10,
        textAlign: "center",
      },
      radioButton: {
        width: 15,
        height: 15,
        borderRadius: 10,
        backgroundColor: AppColors.appViolentColor_6D08C3,
        position: "absolute",
        top: 10,
        right: 10,
      },
      // vsText: {
      //   fontSize: 20,
      //   fontWeight: "bold",
      //   color: AppColors.white,
      //   marginHorizontal: 10,
      // },
      timerText: {
        fontSize: 14,
        color: AppColors.white,
        marginBottom: 15,
      },
      buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "40%",
      },
      button: {
        flex: 1,
        paddingVertical: 10,
        borderRadius: 10,
        alignItems: "center",
        marginHorizontal: 5,
      },
      abstainButton: {
        backgroundColor: "transparent",
        borderWidth: 2,
        borderColor: AppColors.appViolentColor_6D08C3,
      },
      abstainText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
      voteButton: {
        backgroundColor: AppColors.appViolentColor_6D08C3,
      },
      voteText: {
        color: AppColors.white,
        fontSize: 14,
        fontWeight: "bold",
      },
    },
  }
);

export default VotePopup;
